/**
 * Form controls.
 *
 * Every control is a real `<input>`, `<textarea>` or `<select>` wearing a
 * border, never a `div` pretending. That is what keeps autofill, form
 * validation, mobile keyboards and screen readers working without any of it
 * being re-implemented here.
 *
 * A label is mandatory. `labelHidden` moves it out of sight but keeps it in the
 * accessibility tree, so there is no way to ship an unlabelled field by
 * accident.
 */

import { forwardRef, useId } from 'react'
import type {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'

import styles from './Fields.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

interface FieldShellProps {
  fieldId: string
  label: string
  labelExtra?: ReactNode
  labelHidden: boolean
  required: boolean
  hint: ReactNode
  error: string | null | undefined
  hintId: string
  errorId: string
  className: string | undefined
  children: ReactNode
}

function FieldShell({
  fieldId,
  label,
  labelExtra,
  labelHidden,
  required,
  hint,
  error,
  hintId,
  errorId,
  className,
  children,
}: FieldShellProps): ReactElement {
  const showHint = hint !== undefined && hint !== null && hint !== ''
  const showError = typeof error === 'string' && error !== ''

  return (
    <div className={cx(styles.field, className)}>
      <div className={styles.labelRow}>
        <label className={cx(styles.label, labelHidden && 'sr-only')} htmlFor={fieldId}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
        {labelExtra !== undefined && labelExtra !== null ? (
          <span className={styles.labelExtra}>{labelExtra}</span>
        ) : null}
      </div>
      {children}
      {showHint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
      {showError ? (
        <p className={styles.error} id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  )
}

function describedBy(
  hint: ReactNode,
  error: string | null | undefined,
  hintId: string,
  errorId: string,
  own: string | undefined,
): string | undefined {
  const ids = [
    own,
    hint !== undefined && hint !== null && hint !== '' ? hintId : undefined,
    typeof error === 'string' && error !== '' ? errorId : undefined,
  ].filter((value): value is string => typeof value === 'string' && value !== '')

  return ids.length === 0 ? undefined : ids.join(' ')
}

interface SharedFieldProps {
  label: string
  labelHidden?: boolean
  hint?: ReactNode
  /** A validation message. Its presence also sets `aria-invalid`. */
  error?: string | null
  /** Applied to the field wrapper, not to the control. */
  className?: string
}

// ── TextField ───────────────────────────────────────────────────────────────

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>,
    SharedFieldProps {
  /** Sits inside the border, before the text — a search glyph, a currency. */
  leading?: ReactNode
  /** Sits inside the border, after the text — a unit, a clear button. */
  trailing?: ReactNode
  /** Optional content aligned to the right of the label, such as a character count. */
  labelExtra?: ReactNode
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    labelHidden = false,
    hint,
    error,
    className,
    leading,
    trailing,
    labelExtra,
    id,
    required = false,
    type = 'text',
    'aria-describedby': ownDescribedBy,
    ...rest
  },
  ref,
) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`
  const invalid = typeof error === 'string' && error !== ''

  return (
    <FieldShell
      fieldId={fieldId}
      label={label}
      labelExtra={labelExtra}
      labelHidden={labelHidden}
      required={required}
      hint={hint}
      error={error}
      hintId={hintId}
      errorId={errorId}
      className={className}
    >
      <div className={cx(styles.control, invalid && styles.invalid)}>
        {leading !== undefined && leading !== null ? (
          <span className={styles.adornment} aria-hidden="true">
            {leading}
          </span>
        ) : null}
        <input
          {...rest}
          ref={ref}
          id={fieldId}
          type={type}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy(hint, error, hintId, errorId, ownDescribedBy)}
          className={styles.input}
        />
        {trailing !== undefined && trailing !== null ? (
          <span className={styles.adornment}>{trailing}</span>
        ) : null}
      </div>
    </FieldShell>
  )
})

// ── TextArea ────────────────────────────────────────────────────────────────

export interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>,
    SharedFieldProps {
  /** Optional content aligned to the right of the label, such as a character count. */
  labelExtra?: ReactNode
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    labelHidden = false,
    hint,
    error,
    className,
    id,
    required = false,
    rows = 4,
    labelExtra,
    'aria-describedby': ownDescribedBy,
    ...rest
  },
  ref,
) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`
  const invalid = typeof error === 'string' && error !== ''

  return (
    <FieldShell
      fieldId={fieldId}
      label={label}
      labelExtra={labelExtra}
      labelHidden={labelHidden}
      required={required}
      hint={hint}
      error={error}
      hintId={hintId}
      errorId={errorId}
      className={className}
    >
      <div className={cx(styles.control, styles.controlBlock, invalid && styles.invalid)}>
        <textarea
          {...rest}
          ref={ref}
          id={fieldId}
          rows={rows}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy(hint, error, hintId, errorId, ownDescribedBy)}
          className={cx(styles.input, styles.textarea)}
        />
      </div>
    </FieldShell>
  )
})

// ── Select ──────────────────────────────────────────────────────────────────

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'children'>,
    SharedFieldProps {
  options?: readonly SelectOption[]
  /** Shown as a non-selectable first row when the value is still empty. */
  placeholder?: string
  children?: ReactNode
}

function ChevronIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M4 6.5 8 10.5 12 6.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    labelHidden = false,
    hint,
    error,
    className,
    options,
    placeholder,
    children,
    id,
    required = false,
    'aria-describedby': ownDescribedBy,
    ...rest
  },
  ref,
) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`
  const invalid = typeof error === 'string' && error !== ''

  return (
    <FieldShell
      fieldId={fieldId}
      label={label}
      labelHidden={labelHidden}
      required={required}
      hint={hint}
      error={error}
      hintId={hintId}
      errorId={errorId}
      className={className}
    >
      <div className={cx(styles.control, invalid && styles.invalid)}>
        <select
          {...rest}
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy(hint, error, hintId, errorId, ownDescribedBy)}
          className={cx(styles.input, styles.select)}
        >
          {placeholder !== undefined ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options?.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
          {children}
        </select>
        <span className={styles.chevron} aria-hidden="true">
          <ChevronIcon />
        </span>
      </div>
    </FieldShell>
  )
})

// ── Checkbox ────────────────────────────────────────────────────────────────

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> {
  label: ReactNode
  hint?: ReactNode
  error?: string | null
  className?: string
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, error, className, id, 'aria-describedby': ownDescribedBy, ...rest },
  ref,
) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`
  const invalid = typeof error === 'string' && error !== ''
  const showHint = hint !== undefined && hint !== null && hint !== ''
  const showError = invalid

  return (
    <div className={cx(styles.field, className)}>
      <div className={styles.checkboxRow}>
        <input
          {...rest}
          ref={ref}
          id={fieldId}
          type="checkbox"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy(hint, error, hintId, errorId, ownDescribedBy)}
          className={styles.checkbox}
        />
        <label className={styles.checkboxLabel} htmlFor={fieldId}>
          {label}
        </label>
      </div>
      {showHint ? (
        <p className={cx(styles.hint, styles.checkboxHint)} id={hintId}>
          {hint}
        </p>
      ) : null}
      {showError ? (
        <p className={cx(styles.error, styles.checkboxHint)} id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  )
})
